import { clamp, toFloat64 } from './util.ts';

/** A single biquad section with a0 normalised to 1. */
export interface Biquad {
  b0: number;
  b1: number;
  b2: number;
  a1: number;
  a2: number;
}

/** RBJ cookbook low-pass. */
export function designLowpass(cutoffHz: number, sampleRate: number, q: number): Biquad {
  const fc = clamp(cutoffHz, 1, sampleRate * 0.49);
  const w0 = (2 * Math.PI * fc) / sampleRate;
  const cw = Math.cos(w0);
  const alpha = Math.sin(w0) / (2 * q);
  const a0 = 1 + alpha;
  return {
    b0: ((1 - cw) / 2) / a0,
    b1: (1 - cw) / a0,
    b2: ((1 - cw) / 2) / a0,
    a1: (-2 * cw) / a0,
    a2: (1 - alpha) / a0,
  };
}

/** RBJ cookbook high-pass. */
export function designHighpass(cutoffHz: number, sampleRate: number, q: number): Biquad {
  const fc = clamp(cutoffHz, 1, sampleRate * 0.49);
  const w0 = (2 * Math.PI * fc) / sampleRate;
  const cw = Math.cos(w0);
  const alpha = Math.sin(w0) / (2 * q);
  const a0 = 1 + alpha;
  return {
    b0: ((1 + cw) / 2) / a0,
    b1: (-(1 + cw)) / a0,
    b2: ((1 + cw) / 2) / a0,
    a1: (-2 * cw) / a0,
    a2: (1 - alpha) / a0,
  };
}

/**
 * Q values of the biquad sections of an even-order Butterworth response.
 * Section k of an order-N filter sits at Q = 1 / (2*cos((2k+1)*pi / 2N)).
 */
function butterworthQs(order: number): number[] {
  const n = Math.max(2, Math.round(order / 2) * 2);
  const qs: number[] = [];
  for (let k = 0; k < n / 2; k++) {
    qs.push(1 / (2 * Math.cos(((2 * k + 1) * Math.PI) / (2 * n))));
  }
  return qs;
}

export function butterworthLowpass(cutoffHz: number, sampleRate: number, order = 4): Biquad[] {
  return butterworthQs(order).map((q) => designLowpass(cutoffHz, sampleRate, q));
}

export function butterworthHighpass(cutoffHz: number, sampleRate: number, order = 4): Biquad[] {
  return butterworthQs(order).map((q) => designHighpass(cutoffHz, sampleRate, q));
}

/**
 * Band-pass as a Butterworth high-pass and low-pass in series.
 *
 * Not an IEC 61260 class-1 filter - it is a phone app, not a sound level
 * meter - but the skirts are steep enough that octave-band decay slopes do
 * not bleed into each other.
 */
export function butterworthBandpass(
  lowHz: number,
  highHz: number,
  sampleRate: number,
  order = 4,
): Biquad[] {
  const nyquist = sampleRate / 2;
  const sections: Biquad[] = [];
  if (lowHz > 0) sections.push(...butterworthHighpass(lowHz, sampleRate, order));
  if (highHz < nyquist * 0.98) sections.push(...butterworthLowpass(highHz, sampleRate, order));
  return sections;
}

/** Single forward pass through a cascade. Returns a new array. */
export function applyBiquads(x: ArrayLike<number>, sections: Biquad[]): Float64Array {
  const out = toFloat64(x).slice();
  for (const s of sections) {
    let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
    for (let i = 0; i < out.length; i++) {
      const x0 = out[i];
      const y0 = s.b0 * x0 + s.b1 * x1 + s.b2 * x2 - s.a1 * y1 - s.a2 * y2;
      x2 = x1; x1 = x0;
      y2 = y1; y1 = y0;
      out[i] = y0;
    }
  }
  return out;
}

/**
 * Zero-phase filtering: forward pass, reverse pass, with edge padding.
 *
 * Two things this buys over a plain forward filter. Group delay disappears,
 * which matters because a causal filter slides the decay curve in time and
 * biases RT60 short in the high bands and long in the low ones. And the
 * effective order doubles.
 *
 * The padding is not optional. Each pass starts with zeroed state, so a plain
 * forward-and-back filtfilt leaves a settling transient at *both* ends of the
 * result - and on a range profile that end transient reads as a colossal echo
 * at maximum range, tens of dB above anything real. The signal is extended by
 * odd reflection through its endpoints before filtering and trimmed after, so
 * the filter settles on invented samples instead of on the measurement.
 */
export function filtfilt(x: ArrayLike<number>, sections: Biquad[]): Float64Array {
  if (sections.length === 0) return toFloat64(x).slice();
  const n = x.length;
  if (n < 4) return toFloat64(x).slice();

  const pad = Math.min(n - 1, settlingLength(sections));
  const padded = new Float64Array(n + 2 * pad);
  // Odd extension: mirror through the endpoint value, so the padding continues
  // the local slope instead of introducing a corner the filter would ring on.
  const first = x[0];
  const last = x[n - 1];
  for (let i = 0; i < pad; i++) padded[i] = 2 * first - x[pad - i];
  for (let i = 0; i < n; i++) padded[pad + i] = x[i];
  for (let i = 0; i < pad; i++) padded[pad + n + i] = 2 * last - x[n - 2 - i];

  const forward = applyBiquads(padded, sections);
  reverseInPlace(forward);
  const backward = applyBiquads(forward, sections);
  reverseInPlace(backward);

  return backward.slice(pad, pad + n);
}

/**
 * Samples needed for the slowest section to decay to -100 dB.
 *
 * A biquad's poles sit at radius sqrt(a2), so its impulse response decays by
 * that factor per sample; the settling length follows directly.
 */
function settlingLength(sections: Biquad[]): number {
  let longest = 0;
  for (const s of sections) {
    const radius = Math.sqrt(Math.max(Math.abs(s.a2), 1e-12));
    if (radius >= 1) return 4096;
    longest = Math.max(longest, Math.log(1e-5) / Math.log(radius));
  }
  return Math.max(32, Math.ceil(longest * sections.length));
}

function reverseInPlace(x: Float64Array): void {
  for (let i = 0, j = x.length - 1; i < j; i++, j--) {
    const t = x[i];
    x[i] = x[j];
    x[j] = t;
  }
}

/** ISO octave band centres, in Hz. */
export const OCTAVE_BANDS = [125, 250, 500, 1000, 2000, 4000, 8000] as const;

export interface OctaveBand {
  centerHz: number;
  lowHz: number;
  highHz: number;
  label: string;
}

export function octaveBand(centerHz: number): OctaveBand {
  const r = Math.SQRT2;
  return {
    centerHz,
    lowHz: centerHz / r,
    highHz: centerHz * r,
    label: centerHz >= 1000 ? `${centerHz / 1000}k` : `${centerHz}`,
  };
}

/** Octave bands that fit inside the measured band and below Nyquist. */
export function usableOctaveBands(sampleRate: number, sweepLowHz: number, sweepHighHz: number): OctaveBand[] {
  return OCTAVE_BANDS.map(octaveBand).filter(
    (b) => b.lowHz >= sweepLowHz * 0.9 && b.highHz <= Math.min(sweepHighHz * 1.05, sampleRate * 0.45),
  );
}
